<script lang="ts">
  import type { Snippet } from "svelte";
  import Form from "../components/Form.svelte";
  import DataForm from "../lib/dataform/DataForm.svelte";
  import type { JSONObject } from "../lib/dataform/dataFormLib.svelte";
  import Workflow from "../lib/workflow/Workflow.svelte";
  import Step from "../lib/workflow/Step.svelte";
  import RadioGroupField from "../components/uiform/RadioGroupField.svelte";
  import CheckboxField from "../components/uiform/CheckboxField.svelte";
  import TextField from "../components/uiform/TextField.svelte";
  import DateField from "../components/uiform/DateField.svelte";
  import CloseButton from "../components/CloseButton.svelte";
  import ContinueButton from "../components/workflow/ContinueButton.svelte";
  import WorkflowFooter from "../components/uiform/WorkflowFooter.svelte";
  import { localizeDate } from "../lib/date";

  let {
    action,
    data = {response:{}},
    errors = {response:{}},
    declarations = {}
  } : {
    action: string;
    data?: JSONObject;
    errors?: JSONObject;
    declarations?: Record<string, Snippet>;
  } = $props()

  let value = $state<JSONObject>()
  let declined = $derived(value?.wish_to_claim == "no")
  let hasPlus = $derived(value?.is_plus_member == "yes")
  let travelName = $derived([value?.travel_firstname, value?.travel_surname].filter(Boolean).join(" "))
  let accountNumber = $derived(hasPlus ? (value?.plus_membership_number || "Unknown") : "N/A")

  const scrollToTop = () => document.querySelector("#task-modal .modal-box")?.scrollTo(0, 0);
</script>
<Form {action} method="put" class="ui-rhythm">
  <DataForm {data} {errors} name="response" bind:value>
    <Workflow onchange={scrollToTop}>
      <Step>
        <h2 class="ui-title">Your settlement includes a free year of easyJet Plus</h2>
        <RadioGroupField name="wish_to_claim" options={[["Yes", "yes"], ["No", "no"]]} required>
          {#snippet legend()}Do you wish to claim your free one-year <nobr>easyJet Plus</nobr> membership?{/snippet}
        </RadioGroupField>
        <WorkflowFooter back={false}>
          <CloseButton />
          <ContinueButton {...(declined ? {label: "Done", name: "complete", value: "1"} : {})} />
        </WorkflowFooter>
      </Step>

      <Step disabled={!declined}>
        <h2 class="ui-title">easyJet Plus declined</h2>
        <p>If you change your mind, you may return to your client portal and re-open this task until <nobr>11 December 2026.</nobr></p>
        <WorkflowFooter><CloseButton class="btn-primary" /></WorkflowFooter>
      </Step>

      <!-- Everything below is unreachable when declined -->
      <Step>
        <h2 class="ui-title">Confirm legal name and birthdate</h2>
        <p>Please ensure that these answers are the correct values shown on your passport or travel ID.</p>
        <TextField name="travel_firstname" legend="First name(s)" required />
        <TextField name="travel_surname" legend="Surname" required />
        <DateField name="dob" legend="Date of birth" required />
        <WorkflowFooter><ContinueButton /></WorkflowFooter>
      </Step>

      <Step>
        <h2 class="ui-title">Your easyJet account</h2>
        <RadioGroupField name="is_plus_member" options={[["Yes", "yes"], ["No", "no"]]} required>
          {#snippet legend()}Are you currently an <nobr>easyJet Plus</nobr> member?{/snippet}
        </RadioGroupField>
        <WorkflowFooter><ContinueButton label="Continue" /></WorkflowFooter>
      </Step>

      <Step disabled={!hasPlus}>
        <h2 class="ui-title">Your easyJet account</h2>
        <TextField name="account_name" legend="What name is shown on your easyJet account?" required />
        <TextField name="account_email" type="email" legend="Which email address do you use with your existing easyJet Plus membership?" required />
        <WorkflowFooter><ContinueButton /></WorkflowFooter>
      </Step>

      <Step disabled={!hasPlus}>
        <h2 class="ui-title">Your easyJet account</h2>
        <p>These answers help make sure easyJet adds the free year to the right account, but you can leave them blank if you don't know.</p>
        <TextField name="plus_membership_number" legend="What is your easyJet Plus membership number?" />
        <DateField name="plus_membership_end" legend="When does your current membership end?" />
        <WorkflowFooter><ContinueButton /></WorkflowFooter>
      </Step>

      <Step disabled={hasPlus}>
        <h2 class="ui-title">Create your easyJet account</h2>
        <TextField
          name="account_email"
          type="email"
          legend="Which email address would you like to use to create your easyJet account and easyJet Plus membership?"
          note="This must be a personal email address that only you use."
          required
        />
        <WorkflowFooter><ContinueButton /></WorkflowFooter>
      </Step>

      <Step>
        <h2 class="ui-title">Declarations</h2>
        <div class="ui-alert ui-alert-info text-base">
          <div>
            <p><b>Name:</b> {travelName}<br><b>Date of birth:</b> {localizeDate(value?.dob as string)}</p>
            <p><b>Email:</b> {value?.account_email}<br><b>easyJet account number:</b> {accountNumber}</p>
          </div>
        </div>
        <p>Please read and tick each statement to confirm.</p>
        {#each Object.entries(declarations) as [name, label] (name)}
          <CheckboxField {name} {label} required />
        {/each}
        <WorkflowFooter><ContinueButton label="Claim easyJet Plus" name="complete" value="1" /></WorkflowFooter>
      </Step>

      <Step>
        <h2 class="ui-title">easyJet Plus claimed!</h2>
        <p>
          You will receive an email from easyJet at <b>{value?.account_email}</b> about your new or extended
          <nobr>easyJet Plus</nobr> membership.
        </p>
        <p>
          Please allow time for all affected clients to make their claim and for easyJet’s processing.
          Expect your easyJet Plus email in <b>mid-January</b>.
        </p>
        <WorkflowFooter back={false}><CloseButton class="btn-primary w-full" /></WorkflowFooter>
      </Step>
    </Workflow>
  </DataForm>
</Form>
